import { useState } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import {
  CERTIFICATION_PROGRAMS,
  DEMO_CERT_STATUSES,
  DEMO_USER,
  levelColor,
  statusColor,
  type CertificationLevel,
  type ProgramStatus,
} from "../lib/certifications";
import { submitCapstone } from "../lib/sentinel-api";
import { ScenarioSimulator, type ScenarioEvent, type ScenarioAction } from "../components/ScenarioSimulator";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  const labels: Record<ProgramStatus, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    expired: "Expired",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.25rem 0.625rem",
        borderRadius: "var(--radius-full)",
        fontSize: "0.6875rem",
        fontWeight: 600,
        background: `${statusColor(status)}15`,
        color: statusColor(status),
      }}
    >
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(status) }} />
      {labels[status]}
    </span>
  );
}

export default function CapstonesPage() {
  const [activeCapstoneId, setActiveCapstoneId] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState<string | null>(null);
  const [showSimulation, setShowSimulation] = useState(false);

  async function handleLaunch(capstoneId: string, programId: string) {
    setLaunching(true);
    setLaunchResult(null);
    const result = await submitCapstone(DEMO_USER.id, programId, capstoneId, 85, "completed");
    setLaunching(false);
    if (result && !(result as { error?: string }).error) {
      setLaunchResult("Capstone submitted successfully! Score: 85%");
    } else {
      setLaunchResult("Capstone submission completed.");
    }
    setTimeout(() => setLaunchResult(null), 5000);
  }

  // Get programs that have capstones and user has progress on
  const userPrograms = CERTIFICATION_PROGRAMS.filter((p) =>
    DEMO_CERT_STATUSES.some((c) => c.program_id === p.id),
  );

  const activeCapstone = activeCapstoneId
    ? userPrograms.find((p) => p.capstone.id === activeCapstoneId)
    : null;
  const activeCert = activeCapstoneId
    ? DEMO_CERT_STATUSES.find((c) => c.program_id === activeCapstone?.id)
    : null;
  const isCompleted = activeCert?.capstone_status === "completed";

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Capstone Simulations</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Launch and review your capstone simulations. Each capstone validates your ability to apply the Sentinel Safety Architecture in real-world scenarios.
        </p>
      </div>

      {!activeCapstone ? (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Available Capstones</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {userPrograms.map((program) => {
              const cert = DEMO_CERT_STATUSES.find((c) => c.program_id === program.id);
              const capstoneStatus = cert?.capstone_status || "not_started";
              return (
                <div key={program.capstone.id} className="card card-hover" style={{ padding: "var(--space-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{program.capstone.title}</div>
                      <div style={{ display: "flex", gap: "0.375rem", alignItems: "center", marginBottom: "0.5rem" }}>
                        <LevelPill level={program.level} />
                        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{program.industry}</span>
                      </div>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.5rem" }}>{program.capstone.description}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
                      <StatusBadge status={capstoneStatus} />
                      {capstoneStatus === "completed" ? (
                        <button
                          onClick={() => setActiveCapstoneId(program.capstone.id)}
                          className="btn btn-secondary"
                          style={{ fontSize: "0.8125rem" }}
                        >
                          View Results
                        </button>
                      ) : (
                        <button
                          onClick={() => setActiveCapstoneId(program.capstone.id)}
                          className="btn btn-primary"
                          style={{ fontSize: "0.8125rem" }}
                        >
                          Launch
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <button
            onClick={() => setActiveCapstoneId(null)}
            style={{ background: "transparent", border: "none", color: "var(--accent-600)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", marginBottom: "0.5rem" }}
          >
            &larr; Back to Capstones
          </button>
          <h2 style={{ fontSize: "1.25rem", marginBottom: "0.25rem" }}>{activeCapstone.capstone.title}</h2>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>{activeCapstone.capstone.description}</p>

          {/* Simulation view */}
          <div className="card" style={{ padding: "var(--space-5)" }}>
            <div
              style={{
                padding: "var(--space-4)",
                borderRadius: "var(--radius-lg)",
                background: "linear-gradient(135deg, var(--primary-50), var(--accent-50))",
                border: "1px solid var(--accent-200)",
                marginBottom: "var(--space-4)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>&#127918;</div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.375rem" }}>Simulation Engine</h3>
              <p className="text-muted" style={{ fontSize: "0.875rem", maxWidth: "500px", margin: "0 auto" }}>
                The simulation engine is powered by Wix Online Programs. You will be presented with a scenario-based simulation where you must apply Sentinel architecture principles to resolve safety incidents.
              </p>
            </div>

            <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "0.5rem" }}>Instructions</h3>
            <ul style={{ display: "flex", flexDirection: "column", gap: "0.375rem", marginBottom: "var(--space-4)" }}>
              {[
                "Review the scenario brief and available sensor data",
                "Identify risks using the Sentinel diagnostic framework",
                "Select and activate appropriate intervention templates",
                "Verify intervention outcomes and generate reports",
                "Submit your simulation for evaluation",
              ].map((step, i) => (
                <li key={step} style={{ display: "flex", gap: "0.5rem", fontSize: "0.875rem" }}>
                  <span style={{ color: "var(--accent-500)", fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>

            {isCompleted && !showSimulation ? (
              <div style={{ marginBottom: "var(--space-4)" }}>
                <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--accent-50)", border: "1px solid var(--accent-200)", marginBottom: "var(--space-3)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--accent-600)", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ fontSize: "1.125rem" }}>&#10003;</span> Capstone Completed
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Score: {activeCert?.exam_score ?? 85}% | Status: Passed</div>
                </div>
                <button onClick={() => setShowSimulation(true)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>
                  Replay Simulation
                </button>
              </div>
            ) : null}

            {showSimulation && activeCapstone ? (
              <ScenarioSimulator
                title={activeCapstone.capstone.title}
                description={activeCapstone.capstone.description}
                events={[
                  { id: "evt-1", timestamp: "00:00", type: "warning", message: `Sensor anomaly detected in ${activeCapstone.industry} infrastructure`, domain: "Sensors" },
                  { id: "evt-2", timestamp: "00:02", type: "alert", message: "Multi-domain correlation: threat pattern emerging", domain: "Diagnostics" },
                  { id: "evt-3", timestamp: "00:04", type: "critical", message: "Cascade failure risk: connected systems showing stress", domain: "Cascade" },
                  { id: "evt-4", timestamp: "00:06", type: "critical", message: "Full-scale incident imminent — immediate intervention required", domain: "Alert" },
                ]}
                actions={[
                  { id: "act-1", label: "Deploy Monitoring Drones", description: "Increase sensor coverage", cost: 20, effectiveness: 15, consequences: "Expanded awareness across affected domains" },
                  { id: "act-2", label: "Activate Intervention Template", description: "Pre-configured response for this threat type", cost: 30, effectiveness: 25, consequences: "Coordinated multi-domain response deployed" },
                  { id: "act-3", label: "Isolate Affected Systems", description: "Prevent cascade to connected systems", cost: 25, effectiveness: 20, consequences: "Cascade risk reduced — affected systems contained" },
                  { id: "act-4", label: "Full Emergency Response", description: "Maximum response — all resources deployed", cost: 50, effectiveness: 40, consequences: "All available resources committed to threat neutralization" },
                ]}
                onComplete={(score, passed) => { if (passed) handleLaunch(activeCapstone.capstone.id, activeCapstone.id); }}
                onClose={() => setShowSimulation(false)}
              />
            ) : !isCompleted ? (
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button onClick={() => setShowSimulation(true)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Launch Simulation</button>
                <button onClick={() => setActiveCapstoneId(null)} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Cancel</button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </PlatformLayout>
  );
}
